import { createFeatureFlagService } from "../../services/feature-flags";
import { exportRunMarkdown, runExportFileName } from "./export";
import {
  canAdvance,
  createRunState,
  formatElapsed,
  runElapsedMs,
  stepCountdown,
  stepElapsedMs,
  traceGateState,
  transitionRun,
  type BattleProcedureAction,
} from "./model";
import {
  battleProcedureRunSteps,
  battleProcedureSequence,
  type BattleProcedureStep,
} from "./steps";

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

function button(label: string, role: string): HTMLButtonElement {
  const control = element("button", label);
  control.type = "button";
  control.dataset.role = role;
  return control;
}

function captureLabel(step: BattleProcedureStep): string {
  switch (step.capture) {
    case "orders-playback":
      // R4.0 does not reach the orders delivery path. Recorded as a
      // degradation rather than silently presented as working.
      return "Orders are read from your own copy for this step. Record what you took down.";
    case "trainee-output":
      return "Type what you issue. It is captured with a timestamp and exported, and it is not scored.";
    case "notes":
      return "Notes for this step. Captured and exported, not scored.";
  }
}

/**
 * The R4.0 runner. Sequences the authorised battle-procedure steps, shows a
 * master clock and a per-step clock, captures what the trainee types, and
 * exports the run as Markdown. It does not score anything.
 */
export function mountBattleProcedureRunner(container: HTMLElement): void {
  const flags = createFeatureFlagService();
  const steps = battleProcedureRunSteps;
  let state = createRunState(steps);
  let startedAt: string | null = null;
  let finishedAt: string | null = null;

  const frame = element("section");
  frame.className = "bp-frame";
  frame.dataset.role = "battle-procedure";

  const clocks = element("header");
  clocks.className = "bp-clocks";
  const runClock = element("p", "00:00:00");
  runClock.dataset.role = "bp-run-clock";
  const runClockLabel = element("span", "Total");
  const stepClock = element("p", "00:00:00");
  stepClock.dataset.role = "bp-step-clock";
  const stepClockLabel = element("span", "This step");
  const runBlock = element("div");
  runBlock.className = "bp-clock";
  runBlock.append(runClockLabel, runClock);
  const stepBlock = element("div");
  stepBlock.className = "bp-clock";
  stepBlock.append(stepClockLabel, stepClock);
  clocks.append(runBlock, stepBlock);

  const list = element("ol");
  list.className = "bp-step-list";
  list.dataset.role = "bp-step-list";
  const listItems = steps.map((step) => {
    const item = element("li", `${String(step.number)}. ${step.title}`);
    item.dataset.stepId = step.id;
    item.dataset.phase = step.phase;
    list.append(item);
    return item;
  });

  const panel = element("section");
  panel.className = "bp-panel";
  const phase = element("p");
  phase.className = "bp-phase";
  phase.dataset.role = "bp-phase";
  const heading = element("h3");
  heading.dataset.role = "bp-step-title";
  const guidance = element("p");
  guidance.className = "bp-guidance";
  const countdown = element("p");
  countdown.className = "bp-countdown";
  countdown.dataset.role = "bp-countdown";
  countdown.hidden = true;
  // Doc 03 section 7.3. The trace is hidden until the run issues it, then
  // stays visible. Orders are locked for fifteen minutes from the reveal.
  //
  // Ruling 17 parks the runner calling the orders reader, so this is the seam:
  // the runner owns the gate and states it, and the trainee starts delivery
  // himself in the Orders Reader once it lifts.
  const traceGate = element("p");
  traceGate.className = "bp-trace-gate";
  traceGate.dataset.role = "bp-trace-gate";
  traceGate.hidden = true;
  const entry = element("textarea");
  entry.dataset.role = "bp-entry";
  entry.setAttribute("aria-label", "Step entry");
  entry.rows = 8;

  const confirmWrapper = element("label");
  confirmWrapper.className = "bp-confirm";
  const confirm = element("input");
  confirm.type = "checkbox";
  confirm.dataset.role = "bp-confirm";
  const confirmText = element("span");
  confirmWrapper.append(confirm, confirmText);
  confirmWrapper.hidden = true;

  const advance = button("Next step", "bp-advance");
  const start = button("Start battle procedure", "bp-start");
  const exportRun = button("Export this run", "bp-export");
  const status = element("p");
  status.className = "bp-status";
  status.dataset.role = "bp-status";

  const controls = element("div");
  controls.className = "bp-controls";
  controls.append(start, advance, exportRun);

  panel.append(
    phase,
    heading,
    guidance,
    traceGate,
    countdown,
    entry,
    confirmWrapper,
    controls,
    status,
  );

  const workspace = element("div");
  workspace.className = "bp-workspace";
  workspace.append(list, panel);
  frame.append(clocks, workspace);

  if (!flags.isEnabled("battleProcedure")) {
    container.append(
      element("p", "The battle procedure runner is not enabled in this build."),
    );
    return;
  }
  container.append(frame);

  const dispatch = (action: BattleProcedureAction): void => {
    state = transitionRun(state, action, Date.now(), steps);
    render();
  };

  function currentStep(): BattleProcedureStep | undefined {
    return steps[state.currentIndex];
  }

  function renderClocks(): void {
    const nowMs = Date.now();
    runClock.textContent = formatElapsed(runElapsedMs(state, nowMs));
    stepClock.textContent = formatElapsed(
      stepElapsedMs(state, state.currentIndex, nowMs),
    );
    const gate = traceGateState(
      state,
      nowMs,
      battleProcedureSequence.traceGate.lockMinutes,
    );
    traceGate.hidden = !gate.traceVisible;
    traceGate.dataset.status = gate.ordersUnlocked ? "unlocked" : "locked";
    traceGate.textContent = gate.ordersUnlocked
      ? "Trace issued. The fifteen minutes are up: start orders delivery in the Orders Reader."
      : `Trace issued. Mark it. Orders delivery is locked for another ${formatElapsed(gate.remainingMs)}.`;
    const remaining = stepCountdown(state, state.currentIndex, nowMs, steps);
    if (remaining === null) {
      countdown.hidden = true;
      return;
    }
    countdown.hidden = false;
    countdown.dataset.status = remaining.withinStandard ? "within" : "exceeded";
    countdown.textContent = remaining.withinStandard
      ? `${formatElapsed(remaining.remainingMs)} left of the ${formatElapsed(remaining.standardMs)} standard.`
      : `${formatElapsed(-remaining.remainingMs)} over the ${formatElapsed(remaining.standardMs)} standard.`;
  }

  function render(): void {
    const step = currentStep();
    const record = state.steps[state.currentIndex];
    frame.dataset.status = state.status;

    for (const [index, item] of listItems.entries()) {
      item.dataset.state =
        index < state.currentIndex || state.status === "finished"
          ? "done"
          : index === state.currentIndex && state.status === "running"
            ? "current"
            : "pending";
    }

    if (step === undefined || record === undefined) return;

    phase.textContent = `${step.phase} — step ${String(step.number)} of ${String(battleProcedureSequence.steps.length)}`;
    heading.textContent = step.title;
    guidance.textContent = captureLabel(step);
    if (entry.value !== record.text) entry.value = record.text;

    const mandatory = step.requiresConfirmation === true;
    confirmWrapper.hidden = !mandatory;
    confirmText.textContent = step.confirmationPrompt ?? "";
    confirm.checked = record.confirmed;
    confirm.disabled = record.confirmed || state.status !== "running";

    start.hidden = state.status !== "idle";
    entry.disabled = state.status !== "running";
    advance.disabled = !canAdvance(state, steps);
    advance.textContent =
      state.currentIndex === steps.length - 1 ? "Finish run" : "Next step";
    advance.hidden = state.status !== "running";

    status.textContent =
      state.status === "idle"
        ? `Ready. ${String(steps.length)} steps, ${battleProcedureSequence.runScope.start.toLowerCase()} through ${battleProcedureSequence.runScope.end.toLowerCase()}.`
        : state.status === "finished"
          ? "Run complete. Export it before you close this page."
          : mandatory && !record.confirmed
            ? "This step cannot be passed until you confirm it."
            : "Running.";

    renderClocks();
    syncTick();
  }

  start.addEventListener("click", () => {
    startedAt = new Date().toISOString();
    dispatch({ type: "START" });
  });

  advance.addEventListener("click", () => {
    const last = state.currentIndex === steps.length - 1;
    dispatch({ type: "ADVANCE" });
    if (last) finishedAt = new Date().toISOString();
  });

  entry.addEventListener("input", () => {
    const step = currentStep();
    if (step === undefined) return;
    dispatch({ type: "SET_TEXT", stepId: step.id, text: entry.value });
  });

  confirm.addEventListener("change", () => {
    const step = currentStep();
    if (step === undefined || !confirm.checked) return;
    dispatch({ type: "CONFIRM", stepId: step.id });
  });

  exportRun.addEventListener("click", () => {
    const markdown = exportRunMarkdown(state, battleProcedureSequence, steps, {
      finishedAt,
      nowMs: Date.now(),
      startedAt,
    });
    // Local file only. Nothing here is uploaded.
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = element("a");
    link.href = url;
    link.download = runExportFileName(startedAt);
    link.click();
    URL.revokeObjectURL(url);
    status.textContent = "Run exported to your downloads.";
  });

  // Bounded: the clock ticks only while a run is running, and stops when it
  // finishes. Nothing polls on an idle page.
  let tick: number | undefined;
  const stopTick = (): void => {
    if (tick === undefined) return;
    window.clearInterval(tick);
    tick = undefined;
  };
  const syncTick = (): void => {
    if (state.status === "running" && tick === undefined) {
      tick = window.setInterval(() => {
        if (state.status === "running") renderClocks();
        else stopTick();
      }, 1000);
      return;
    }
    if (state.status !== "running") stopTick();
  };

  render();
}
